import * as React from 'react';
import { useDispatch } from 'react-redux';
import { Button, Modal } from '@rtkwlf/ui';
import { apiRawFetch, apiRequest } from '../../../APIClient';
import { AgentRow } from '../../../types';
import { RISK_ACTIONS } from '../../../actions/risk';
import { useTrashablePromise } from '../../../hooks';

export const useDeactivateAgent = () => {
  const [deactivateAgent, setDeactivateAgent] = React.useState<AgentRow | null>(
    null,
  );

  const closeModal = React.useCallback(() => {
    setDeactivateAgent(null);
  }, []);

  const onConfirmDeactivate = React.useCallback((agent: AgentRow) => {
    setDeactivateAgent(agent);
  }, []);

  const { registerPromise } = useTrashablePromise();
  const dispatch = useDispatch();

  const onDeactivate = React.useCallback(async () => {
    if (!deactivateAgent) {
      return;
    }

    try {
      await registerPromise(
        apiRawFetch.deactivateAgentById(deactivateAgent.id),
      );

      dispatch(RISK_ACTIONS.DEACTIVATE_AGENT(deactivateAgent.id));

      const message = 'Offline endpoint has been removed.';
      dispatch(apiRequest.setGlobalStatus(message, 'success'));
    } catch (err) {
      dispatch(apiRequest.setGlobalStatus(err.message, err.color || 'danger'));
    } finally {
      setDeactivateAgent(null);
    }
  }, [deactivateAgent, dispatch, registerPromise]);

  const modal: React.ReactNode = React.useMemo(() => {
    if (!deactivateAgent) {
      return null;
    }

    return (
      <Modal
        visible
        title={<span style={{ color: '#000' }}>Remove Offline Endpoint</span>}
        onCancel={closeModal}
        onOk={onDeactivate}
        footer={
          <>
            <Button
              data-cy="cancel-remove-endpoint"
              type="primary"
              ghost
              onClick={closeModal}
            >
              Cancel
            </Button>
            <Button
              type="danger"
              data-cy="confirm-remove-endpoint"
              onClick={onDeactivate}
            >
              Remove Endpoint
            </Button>
          </>
        }
      >
        <div style={{ maxWidth: 600 }}>
          <p>
            Are you sure you want to remove the offline endpoint{' '}
            <b>{deactivateAgent.hostname}</b>?
          </p>
          <p>
            If the endpoint agent is still installed, it will reappear in this
            list.
          </p>
        </div>
      </Modal>
    );
  }, [deactivateAgent, closeModal, onDeactivate]);

  return {
    modal,
    onConfirmDeactivate,
  };
};
